import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  Globe, 
  Mail, 
  Search, 
  TrendingUp, 
  Code, 
  ShoppingCart, 
  Settings, 
  Server,
  MessageCircle,
  BarChart2,
  Rocket,
  Target,
  Clock,
  Users,
  Zap
} from 'lucide-react';

const ServicesPage = () => {
  const containerRef = useRef(null);
  const servicesRef = useRef(null);
  const ctaRef = useRef(null);

  const isServicesInView = useInView(servicesRef, { once: true, margin: "-100px" });
  const isCtaInView = useInView(ctaRef, { once: true, margin: "-100px" });

  const services = [
    {
      title: "Web Development",
      icon: Globe,
      description: "We make websites better.",
      subServices: [
        { name: "Theme Development", icon: Code },
        { name: "Shopify Apps", icon: ShoppingCart },
        { name: "Integration", icon: Settings },
        { name: "Ongoing Maintenance", icon: Server },
        { name: "WordPress", icon: Globe },
        { name: "BigCommerce", icon: ShoppingCart }
      ],
      gradient: "from-[#f4b400] to-[#ffd54f]"
    },
    {
      title: "Retention Marketing",
      icon: Mail,
      description: "We'll make the most out of your list if you already have one, or build one for you if you don't. Klaviyo email and SMS marketing ninjas ready to serve your bottom line…",
      subServices: [
        { name: "Email Marketing", icon: Mail },
        { name: "SMS Campaigns", icon: MessageCircle },
        { name: "List Building", icon: Users },
        { name: "Automation", icon: Zap }
      ],
      gradient: "from-[#81deea] to-[#b3ebf2]"
    },
    {
      title: "SEO & Content Strategies",
      icon: Search,
      description: "Your business or service must show up in Google when people are searching for them. We demystify this process with proven strategies that get results. Onward and upward!",
      subServices: [
        { name: "Search Optimization", icon: Search },
        { name: "Content Planning", icon: BarChart2 },
        { name: "Performance Tracking", icon: TrendingUp },
        { name: "Strategy Development", icon: Target }
      ],
      gradient: "from-[#f4b400] to-[#ffd54f]"
    },
    {
      title: "Paid Ads",
      icon: TrendingUp,
      description: "We love ads like you love to talk about your business. In the GROVIYO ad room, thrive from bringing attention to your service or brand. Let us learn your business and craft ad campaigns that drive sales. Period.",
      subServices: [
        { name: "Campaign Management", icon: Settings },
        { name: "Ad Creation", icon: Rocket },
        { name: "Performance Analysis", icon: BarChart2 },
        { name: "ROI Optimization", icon: TrendingUp }
      ],
      gradient: "from-[#81deea] to-[#b3ebf2]"
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen bg-[#b3ebf2] overflow-hidden">
      {/* Animated background elements */}
      <motion.div className="absolute inset-0">
        <motion.div 
          className="absolute -top-40 -right-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#ffd54f] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#81deea] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear", delay: 10 }}
        />
      </motion.div>

      <motion.div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_2px,transparent_2px),linear-gradient(90deg,rgba(0,0,0,0.02)_2px,transparent_2px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"/>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-16">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-[#f4b400] bg-clip-text text-transparent inline-block p-2 leading-relaxed bg-[length:100%_120%] bg-bottom">
              Our Services
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            With 50+ years of combined sales and marketing expertise, we'll take your business from offline or underperforming to dominating online.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          ref={servicesRef}
          className="grid md:grid-cols-2 gap-8 mb-16"
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isServicesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              <div className={`p-6 bg-gradient-to-r ${service.gradient}`}>
                <service.icon className="w-8 h-8 text-white mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">{service.title}</h2>
                <p className="text-white/90">{service.description}</p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  {service.subServices.map((subService) => (
                    <motion.div
                      key={subService.name}
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center space-x-2 text-gray-600"
                    >
                      <subService.icon className="w-4 h-4 text-[#f4b400]" />
                      <span className="text-sm">{subService.name}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          ref={ctaRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isCtaInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8">
            <h2 className="text-3xl font-bold mb-4 text-gray-800">Time for a change?</h2>
            <p className="text-xl text-gray-600 mb-6">
              We're here to help transform your business with our comprehensive suite of digital services.
            </p>
            <div className="flex items-center justify-center space-x-4">
              <Clock className="w-6 h-6 text-[#f4b400]" />
              <span className="text-gray-600">Ready when you are</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ServicesPage;